<template>
  <div
    class="product-options"
    :class="{
      [$class('loading')]: loading,
      [$class('hidden')]: product?.hasOnlyDefaultVariant && !bundle,
    }"
  >
    <div
      v-for="option of options"
      :key="option.key"
      class="product-options__option"
    >
      <legend
        v-if="!hideLabels"
        class="product-options__option-title text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
      >
        <span>{{ option.name }}</span>
      </legend>
      <div
        v-if="option.display !== 'dropdown' && option.display !== 'bundle'"
        class="product-options__values"
        :class="`product-options__grid-${option.count}`"
      >
        <template v-for="value of option.values" :key="value.key">
          <selection-tab
            v-if="!value.hidden"
            :id="`${value.key}-${uniqueId}`"
            v-model="currentOptions[option.index]"
            class="product-options__value"
            :label="value.value"
            :show-upsizing="
              option.values.filter((optionValue) => !optionValue.hidden)
                .length > 1 &&
              !value.disabled &&
              product.upsizing?.includes(value.value)
            "
            :product="product"
            :variant="variant"
            :featured-image="value.featuredImage"
            :loading="loading"
            :name="option.key"
            :hide-portions-label="hidePortionsLabel"
            :hide-sold-out-label="hideSoldOutLabel"
            :value="value.value"
            :visually-disabled="value.disabled"
            :is-ab-test="isAbTest"
          />
        </template>
      </div>
      <div v-else-if="option.display === 'bundle'">
        <product-bundle-select
          :option="option"
          :loading="loading"
          :product="product"
          @update-bundle-product="handleUpdateOption"
          @update-disabled-status="handleDisableStatus"
        />
      </div>
      <div
        v-else
        class="product-options__dropdown"
        :class="{ 'is-single': isSingleValue }"
      >
        <product-custom-select
          :select-variant="currentOptions[option.index]"
          :options="getSelectOptions(option)"
          :aria-label="option.name"
          :count-label="hideCountLabel ? '' : option.countName"
          :disabled-group-label="$string('product.form.sold_out')"
          :placeholder="$string('product.form.select_variant')"
          :unique-id="`${option.key}-${uniqueId}`"
          :loading="loading"
          :hide-flavor-image="hideFlavorImage || isSizeOption(option.downcase)"
          :hide-flavor-image-on-button="hideFlavorImageOnButton"
          :hide-flavor-badge-on-button="hideFlavorBadgeOnButton"
          :hide-flavor-badge="hideFlavorBadge"
          :force-desktop-dropdown="forceDesktopDropdown"
          :style-full-size="styleFullSize"
          block-class="product-options"
          @update:select-variant="handleOptionChange(option.index, $event)"
          @dropdown-toggled="$emit('dropdown-toggled', $event)"
          @variant-selector-namespace="
            $emit('variant-selector-namespace', $event)
          "
        />
      </div>
    </div>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select"
      :class="$class('hidden')"
      name="id"
      aria-hidden
    >
      <template
        v-for="(variantObject, index) of product.variants"
        :key="getVariantKey(variantObject, index)"
      >
        <option
          v-if="variantObject.availableState !== 'hidden'"
          :value="variantObject.id"
          v-text="`${variantObject.title} - ${variantObject.id}`"
        />
      </template>
    </select>

    <div
      v-if="hasExtraOptions || (product.hasOnlyDefaultVariant && bundle)"
      class="product-options__extra"
    >
      <div
        v-for="bundleItem of offsetBundle"
        :key="bundleItem.name"
        class="product-options__option"
      >
        <div>
          <product-bundle-select
            :option="bundleItem"
            is-extra-option
            :loading="loading"
            :product="product"
            @update-disabled-status="handleDisableStatus"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 * @emits dropdown-toggled - Emit isOpen value (dropdown was opened/closed).
 * @emits variant-selector-namespace - Emit overlay namespace
 *
 */
import SelectionTab from '~async/utils/selection-tab/selection-tab';

import ProductBundleSelect from '~async/product-bundle-select/product-bundle-select.vue';
import ProductCustomSelect from '~global/product-custom-select/product-custom-select';

import { convertToHandle } from '~/helpers/convert';
import { emitUpdateVariant } from '~/helpers/vue';

export default {
  name: 'ProductOptions',

  components: {
    SelectionTab,
    ProductBundleSelect,
    ProductCustomSelect,
  },

  props: {
    bundle: {
      type: [Boolean, Object],
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
        optionStylingTile: [],
        optionStylingImageTile: [],
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variantFromUrl: {
      type: Boolean,
      default: true,
    },
    hideFlavorImage: {
      type: Boolean,
      default: false,
    },
    hideFlavorImageOnButton: {
      type: Boolean,
      default: false,
    },
    hideFlavorBadge: {
      type: Boolean,
      default: false,
    },
    hideFlavorBadgeOnButton: {
      type: Boolean,
      default: false,
    },
    hideCountLabel: {
      type: Boolean,
      default: false,
    },
    hidePortionsLabel: {
      type: Boolean,
      default: false,
    },
    hideLabels: {
      type: Boolean,
      default: true,
    },
    forceDesktopDropdown: {
      type: Boolean,
      default: false,
    },
    hideSoldOutLabel: {
      type: Boolean,
      default: false,
    },
    styleFullSize: {
      type: Boolean,
      default: false,
    },
    uniqueId: {
      type: String,
      default: () => `${Math.random().toString(36).substring(2, 11)}`,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'bundle-extra-item': (payload) => typeof payload === 'object',
    'update-bundle-status': (payload) => typeof payload === 'boolean',
    'dropdown-toggled': (value) => typeof value === 'boolean',
    'variant-selector-namespace': (value) => typeof value === 'string',
  },

  data() {
    return {
      currentOptions: [],
      currentVariantId: 0,
      bundleProducts: Array.from({ length: this.bundle + 1 }, () => ({})),
      hasExtraOptions: false,
      bundleStatus: [],
    };
  },

  computed: {
    isSingleValue() {
      return this.options.every((option) => option.count === 1);
    },
    /**
     * Get offset bundle items.
     * @returns {Array}
     */
    offsetBundle() {
      const leftOverBundle =
        this.options.length === 0
          ? this.bundle
          : this.bundle.slice(this.options.length);

      const offsetBundle = [];
      for (let left = 0; left < leftOverBundle.length; left++) {
        const currentAvailableState = leftOverBundle[
          left
        ].variantsAvailability.find((available) =>
          leftOverBundle[left].variants.some(
            (variant) => available.id === variant.id,
          ),
        );

        const extraOptionMapping = {
          currentOption: leftOverBundle[left].variants[0],
          currentOptionName: leftOverBundle[left].variants[0]?.option1,
          count: 1,
          display: 'bundle',
          downcase: leftOverBundle[left].id.toLowerCase(),
          handle: convertToHandle(leftOverBundle[left].id),
          name: leftOverBundle[left].id,
          key: `${this.section.id}-${this.product.handle}-option-${left}-${convertToHandle(leftOverBundle[left].id)}`,
          availableState: currentAvailableState.availableState,
        };

        offsetBundle.push(extraOptionMapping);
        this.$emit('bundle-extra-item', offsetBundle);
      }

      return offsetBundle;
    },

    /**
     * Get extended options data.
     * @returns {Array}
     */
    options() {
      if (this.loading && (!this.product || this.product.liquid)) {
        return this.getOptionsPlaceholder();
      }

      if (
        (!this.product?.options?.length && !this.bundle) ||
        this.product?.hasOnlyDefaultVariant
      ) {
        return [];
      }

      /**
       * Create deep clone of variants to avoid dirty computed getters.
       * Note: Using JSON.parse/stringify instead of structuredClone because
       * Vue's reactive objects contain non-cloneable properties.
       */
      const variantsClone = JSON.parse(JSON.stringify(this.product.variants));

      /**
       * Get extended options data.
       */

      return this.product.options?.map((option) => {
        const handle = convertToHandle(option.name);
        let currentBundleOption = false;
        let currentBundle = false;
        let extraBundleInfo = false;

        const downcase = option.name.toLowerCase();
        const key = `${this.section.id}-${this.product.handle}-option-${option.index}-${convertToHandle(option.name)}`;
        const disabled = {};
        const hidden = {};
        let display = 'dropdown';

        if (this.section.optionStylingTile?.includes(downcase)) {
          display = 'tile';
        } else if (this.section.optionStylingImageTile?.includes(downcase)) {
          display = 'image';
        } else if (this.bundle && this.bundle.length > 0) {
          display = 'bundle';
        }

        /**
         * Setup current extended data for values updates.
         */
        const optionObject = {
          ...option,
          disabled,
          display,
          downcase,
          handle,
          hidden,
          key,
          originalValues: option.values,
        };

        const values = this.getOptionValues(optionObject, variantsClone);

        // Sort values so sold-out flavors appear at the end
        const sortedValues = values.sort((a, b) => {
          // If both are sold out or both are not sold out, maintain original order
          if (a.soldOut === b.soldOut) {
            return 0;
          }
          // Move sold out items to the end
          return a.soldOut ? 1 : -1;
        });

        let availableState = this.getSoldOutValues(
          sortedValues,
          optionObject.downcase,
        );

        let count = values.filter((value) => !value.hidden).length;
        let countName = this.getOptionCountName(count, downcase);

        if (this.bundle && this.bundle.length > 0) {
          currentBundle = this.bundle.find(
            (bundle) => bundle.id === option.name,
          );

          // for bundles logic to show products that are not part of
          // product property
          if (
            this.product?.options?.length !== this.bundle?.length ||
            (this.product?.options && this.bundle?.length > 0)
          ) {
            this.hasExtraOptions = true;
          }

          currentBundleOption = currentBundle?.variants.find(
            (current) =>
              option.labels.includes(current?.option1) &&
              current.available === true,
          );

          if (!currentBundleOption) {
            currentBundleOption = currentBundle?.variants[0];
          }

          // only available if setup in metaobject, optional values
          extraBundleInfo = {
            name: currentBundle.name,
            type: currentBundle.type,
          };

          // Bundles have no information on variant metafields
          // therefore we get them from the variants in the metaobject
          const getBundleVariantIds = new Set(
            currentBundle.variants.map((variant) => variant.id),
          );

          const filteredAvailability =
            currentBundle.variantsAvailability.filter((variant) =>
              getBundleVariantIds.has(variant.id),
            );

          availableState = filteredAvailability;

          count = currentBundle.variants.filter((variant) => {
            const availability = filteredAvailability.find(
              (state) => state.id === variant.id,
            );

            return availability?.availableState !== 'hidden';
          }).length;
          countName = this.getOptionCountName(count, downcase);
        }

        /**
         * Return extended data.
         */
        return {
          ...optionObject,
          count,
          countName,
          availableState,
          values: sortedValues,
          currentOption: currentBundleOption,
          bundle: currentBundle.variants,
          extraBundleInfo,
        };
      });
    },

    /**
     * Compute size option index.
     * @returns {Number}
     */
    sizeOptionIndex() {
      return this.options.findIndex((option) => {
        return this.$variable('product.sizeOptions').includes(
          option.name.toLowerCase(),
        );
      });
    },

    /**
     * Check if in AB test ESN-18
     */
    isAbTest() {
      const kameleoonExperiment =
        // eslint-disable-next-line
        globalThis.Kameleoon?.API?.Experiments?.getById(345129);
      return Boolean(
        kameleoonExperiment?.associatedVariation?.id === 1200651 &&
        kameleoonExperiment?.active,
      );
    },
  },

  watch: {
    /**
     * Watch current options to update current variant ID based on choices.
     */
    currentOptions: {
      handler() {
        const variantTitle = this.currentOptions.join(' / ');
        const newVariant = this.getVariantFromProperty(variantTitle, 'title');

        if (newVariant && newVariant.availableState !== 'hidden') {
          this.currentVariantId = newVariant.id;
          return;
        }

        /**
         * If previously selected size doesn't exist then try to find default
         * size value.
         * - Will only select if available.
         */
        if (
          this.currentOptions[this.sizeOptionIndex] !==
          this.$variable('product.defaultSizeValue')
        ) {
          const defaultSizeVariantTitle = this.currentOptions
            .map((currentOption, index) => {
              if (index === this.sizeOptionIndex) {
                return this.$variable('product.defaultSizeValue');
              }

              return currentOption;
            })
            .join(' / ');

          const defaultSizeVariant = this.getVariantFromProperty(
            defaultSizeVariantTitle,
            'title',
          );

          if (defaultSizeVariant && defaultSizeVariant.available) {
            this.currentVariantId = defaultSizeVariant.id;
            return;
          }
        }

        /**
         * If default size value doesn't exist in option then find variants
         * which match the first variant (excluding hidden variants).
         */
        const matchingVariants = this.product.variants
          .filter((variant) => variant.availableState !== 'hidden')
          .filter(
            (variant) =>
              variant.selectedOptions[0].value === this.currentOptions[0],
          );

        /**
         * If exact match then ignore availability and select it.
         */
        const exactMatch = matchingVariants.find((variant) => {
          return variant.selectedOptions.every((selectedOption, index) => {
            return selectedOption.value === this.currentOptions[index];
          });
        });

        if (exactMatch) {
          this.currentVariantId = exactMatch.id;
          return;
        }

        /**
         * If no exact match then use first variant if all unavailable
         * - Otherwise use first available.
         */
        const allUnavailable = matchingVariants.every(
          (variant) => !variant.available,
        );

        const fallbackVariant = allUnavailable
          ? matchingVariants[0]
          : matchingVariants.find((variant) => variant.available);

        if (!fallbackVariant) {
          return;
        }

        this.currentVariantId = fallbackVariant.id;
      },
      deep: true,
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return;
      }

      const variant = this.getVariantFromProperty(this.currentVariantId);

      if (!variant) {
        return;
      }

      this.$emit('update-variant', variant);
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      if (this.product.liquid) {
        return;
      }

      this.currentOptions = [];
      this.getCurrentVariant();
    },

    /**
     * Watch variant for updates to update current options.
     */
    variant() {
      if (!this.variant) {
        this.currentVariantId = 0;
      }

      this.getCurrentOptions();
    },
  },

  mounted() {
    this.getCurrentVariant();

    if (
      this.bundle &&
      this.product?.options?.length === 1 &&
      this.product.type === 'bundle'
    ) {
      this.hasExtraOptions = true;
    }
  },

  methods: {
    /**
     * Handle update bundleItem event.
     * @param {Object} bundleItem - Emitted bundleItem object.
     * @param {Number} index - Option index.
     */
    handleUpdateOption(bundleItem, index) {
      // Guard for bundle products: Ensure bundleItem exists and has required properties
      if (!bundleItem || typeof bundleItem !== 'object') {
        if (this.bundle && this.bundle.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            '[product-options] handleUpdateOption called with invalid bundleItem for bundle product',
            { bundleItem, index },
          );
        }
        return;
      }

      if (!bundleItem.option1) {
        if (this.bundle && this.bundle.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            '[product-options] bundleItem missing option1 property',
            { bundleItem, index },
          );
        }
        return;
      }

      this.currentOptions[index] = bundleItem.option1;
      this.selectedVariant = this.variant;
    },

    /**
     * Get option values.
     * @param {Object} option - Current extended option data.
     * @param {Array<Object>} variants - Deep clone of variants.
     * @returns {Array}
     */
    getOptionValues(option, variants) {
      let values = option.values;

      if (this.$variable('product.sizeOptions').includes(option.downcase)) {
        values = this.getSortedContentSizeValues(option.values);
      } else if (
        this.$variable('product.clothingSizeOptions').includes(option.downcase)
      ) {
        values = this.getSortedSizeValues(option.values);
      }

      return values.map((value, index) => {
        const handle = convertToHandle(value);
        const downcase = value.toLowerCase();
        const key = `${option.key}-value-${index}-${handle}`;

        /**
         * Determine value attributes.
         */
        const {
          disabled,
          featuredImage,
          flavorBadge,
          flavorImage,
          hidden,
          soldOut,
        } = this.getValueAttributes(value, option, variants);

        return {
          downcase,
          disabled,
          featuredImage,
          flavorBadge,
          flavorImage,
          handle,
          hidden,
          key,
          soldOut,
          value,
        };
      });
    },

    /**
     * Get current value object.
     * @param {String} value - Value object of search option
     * @returns {Array} - Array of all matching value objects across all options.
     */
    getCurrentValue(value) {
      const matchingValues = this.options.flatMap((opt) =>
        opt.values.filter((val) => val.value === value),
      );

      return matchingValues;
    },

    /**
     * check if option value is sold out for selected option
     * - only if all variants are sold out
     * @param {String} value - option value
     * @returns {Boolean}
     */
    isSoldOutOption(value) {
      const matchingValues = this.getCurrentValue(value);
      if (!matchingValues || matchingValues.length === 0) {
        return false;
      }
      return matchingValues.every((val) => val.soldOut);
    },

    getFlavorImage(value) {
      const matchingValues = this.getCurrentValue(value);

      if (!matchingValues || matchingValues.length === 0) {
        return null;
      }

      // Check if flavorImage is valid (not empty object)
      const isValidImage = (image) => {
        return (
          image &&
          typeof image === 'object' &&
          Object.keys(image).length > 0 &&
          (image.src || image.url)
        );
      };

      // First, try to use current variant's flavorImage if it matches this value
      const isCurrentFlavorValue =
        this.variant?.selectedOptions?.[0]?.value === value;

      if (isCurrentFlavorValue && isValidImage(this.variant?.flavorImage)) {
        return this.variant.flavorImage;
      }

      // Fallback: find any matching value that has a valid image
      const valueWithImage = matchingValues.find((val) =>
        isValidImage(val.flavorImage),
      );

      return valueWithImage?.flavorImage || null;
    },

    /**
     * Get flavor badge for current option value
     * @param {String} value - option value
     * @returns {String|Boolean}
     */
    getFlavorBadge(value) {
      const matchingValues = this.getCurrentValue(value);
      if (!matchingValues || matchingValues.length === 0) {
        return false;
      }

      // Find first value that has a flavorBadge
      const valueWithBadge = matchingValues.find((val) => val.flavorBadge);
      return valueWithBadge?.flavorBadge || false;
    },

    /**
     * Check if option handle is a size option.
     * @param {String} optionDowncase - option name lowercase.
     * @returns {Boolean}
     */
    isSizeOption(optionDowncase) {
      return this.$variable('product.sizeOptions').includes(optionDowncase);
    },

    /**
     * Create custom sort function for values based on sorting array.
     * @param {Array<String>} sortingArray - Array defining sort order.
     * @returns {Function}
     */
    createCustomSort(sortingArray) {
      return (a, b) => {
        const indexA = sortingArray.indexOf(a.toLowerCase());
        const indexB = sortingArray.indexOf(b.toLowerCase());

        return indexA === -1 && indexB === -1
          ? a.toLowerCase().localeCompare(b.toLowerCase())
          : indexA - indexB;
      };
    },

    /**
     * Get custom sorted content size values.
     * - Before they're objects.
     * @param {Array<String>} values - Option values.
     * @returns {Array}
     */
    getSortedContentSizeValues(values) {
      const customSort = this.createCustomSort(
        this.$variable('product.variantSorting'),
      );
      return [...values].sort(customSort);
    },

    /**
     * Get custom sorted size (clothing) values.
     * - Before they're objects.
     * @param {Array<String>} values - Option values.
     * @returns {Array}
     */
    getSortedSizeValues(values) {
      const customSort = this.createCustomSort(
        this.$variable('product.sizeOptionSorting'),
      );
      return [...values].sort(customSort);
    },
    /**
     * Get additional option value attributes based on product filters.
     * - First find variants that match current value.
     * @param {String} value - Current value.
     * @param {Object} option - Current extended option data.
     * @param {Array<Object>} variants - Deep clone of variants.
     * @returns {Object}
     */
    getValueAttributes(value, option, variants) {
      const matchedIndexVariants = variants.filter((variant) => {
        return variant.selectedOptions[option.index].value === value;
      });

      /**
       * Use matched index variant to find top level attributes.
       */
      const soldOut =
        option.index === 0
          ? matchedIndexVariants
              .filter((variant) => variant.availableState !== 'hidden')
              .every((variant) => !variant.available)
          : false;

      const featuredImage =
        option.display === 'image' ? matchedIndexVariants[0].featuredImage : {};

      // Find first variant with a flavorImage, fallback to first variant
      const variantWithFlavorImage =
        matchedIndexVariants.find(
          (variant) =>
            variant.flavorImage &&
            (variant.flavorImage.src || variant.flavorImage.url),
        ) || matchedIndexVariants[0];

      const flavorImage = variantWithFlavorImage?.flavorImage || false;

      // Find first variant with a flavorBadge
      const variantWithFlavorBadge = matchedIndexVariants.find(
        (variant) => variant.flavorBadge,
      );
      const flavorBadge = variantWithFlavorBadge?.flavorBadge || false;

      /**
       * Find variants that match all other currentOptions.
       */
      const matchedVariants = matchedIndexVariants.filter((variant) => {
        if (option.index === 0) {
          return true;
        }

        return this.currentOptions.every(
          (currentOption, currentOptionIndex) => {
            if (!currentOption || option.index === currentOptionIndex) {
              return true;
            }

            return (
              variant.selectedOptions[currentOptionIndex]?.value ===
              currentOption
            );
          },
        );
      });

      return {
        disabled: matchedVariants.every((variant) => !variant.available),
        flavorBadge,
        flavorImage,
        featuredImage,
        hidden: matchedVariants.every(
          (variant) => variant.availableState === 'hidden',
        ),
        soldOut,
      };
    },

    /**
     * Get sold out values.
     * - Sort by size sorting if applicable.
     * @param {Array<Object>} values - Option values.
     * @param {String} option - Downcased option name.
     * @returns {Array}
     */
    getSoldOutValues(values, option) {
      const availableState = values.filter((value) => {
        return value.soldOut && !value.hidden;
      });

      if (!availableState.length) {
        return [];
      }

      const customSort = (a, b) => {
        if (this.$variable('product.clothingSizeOptions').includes(option)) {
          const indexA = this.$variable('product.sizeOptionSorting').indexOf(
            a.downcase,
          );
          const indexB = this.$variable('product.sizeOptionSorting').indexOf(
            b.downcase,
          );

          return indexA === -1 && indexB === -1
            ? a.downcase.localeCompare(b.downcase)
            : indexA - indexB;
        }

        return a.downcase.localeCompare(b.downcase);
      };

      return availableState.sort(customSort);
    },

    /**
     * Handles status from bundle products
     * - if one is soldout/hidden/not available we receive a boolean
     *  @param {Boolean} status - Status of bundle product
     */
    handleDisableStatus(status) {
      this.bundleStatus.push(status);
      const isDisabled = this.bundleStatus.some((state) => state === true);

      /**
       * when one of the values is true
       */
      if (isDisabled) {
        this.$emit('update-bundle-status', true);
      }
    },

    /**
     * Compute option count name depending on option handle.
     * @param {Number} count - Number of products.
     * @param {String} optionDowncase - Option name downcased.
     * @returns {String}
     */
    getOptionCountName(count, optionDowncase) {
      const options = {
        pluralise: count,
        replace: { count },
      };

      if (this.$variable('product.sizeOptions').includes(optionDowncase)) {
        return this.$string('product.variant.option_count_size', options);
      }

      if (this.$variable('product.flavourOptions').includes(optionDowncase)) {
        return this.$string('product.variant.option_count', options);
      }

      if (this.$variable('product.colorOptions').includes(optionDowncase)) {
        return this.$string('product.variant.option_count_color', options);
      }

      return this.$string('product.variant.option_count', options);
    },

    /**
     * Get options placeholder when loading with no existing product data.
     * - Liquid product data doesn't count as it's possibly in the wrong order.
     * @returns {Array}
     */
    getOptionsPlaceholder() {
      if (this.product?.hasOnlyDefaultVariant) {
        return [];
      }

      const hasImageTile = this.product.options.some((option) => {
        return this.section.optionStylingImageTile.includes(
          option.name.toLowerCase(),
        );
      });

      const tileType = hasImageTile ? 'image' : 'tile';

      return Array.from({ length: 2 }, (_, index) => {
        const name = `${this.$string('accessibility.loading')} ${index}`;

        const option = {
          index,
          labels: Array.from(
            { length: 2 },
            (__, labelIndex) => `${name} ${labelIndex}`,
          ),
          name,
          values: Array.from(
            { length: 2 },
            (__, valueIndex) => `${name} ${valueIndex}`,
          ),
        };

        const handle = convertToHandle(option.name);
        const key = `${handle}-key`;

        const values = Array.from({ length: 2 }, (__, valueIndex) => {
          const value = `${this.$string('accessibility.loading')} ${valueIndex}`;
          const valueHandle = convertToHandle(value);
          const valueDowncase = value.toLowerCase();

          return {
            downcase: valueDowncase,
            disabled: false,
            featuredImage: tileType === 'image' ? {} : false,
            handle: valueHandle,
            hidden: false,
            key: valueHandle,
            soldOut: false,
            value,
          };
        });

        return {
          ...option,
          count: 2,
          countName: '',
          disabled: {},
          display: index === 0 ? 'dropdown' : tileType,
          downcase: '',
          handle,
          hidden: {},
          key,
          originalValues: values,
          availableState: [],
          validValues: values,
          values,
        };
      });
    },

    /**
     * Get variant key.
     * @param {Object} variant - Current variant.
     * @param {Number} index - Current index.
     * @returns {String}
     */
    getVariantKey(variant, index) {
      return `${this.product?.handle}-option-${index}-${convertToHandle(variant?.title)}`;
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (
        !this.product ||
        !this.product?.variants?.length ||
        this.product.liquid
      ) {
        return;
      }

      /**
       * Always get current variant from URL.
       * - The URL's variant id may not belong to this product at all (e.g.
       *   a stale `?variant=` param from a different product) — in that case
       *   fall through to the sensible-default logic below instead of
       *   crashing on an unresolved variant.
       */
      const variantFromUrl = this.getVariantFromUrl();
      if (variantFromUrl && this.variantFromUrl) {
        const availableVariant = this.checkAvailability(variantFromUrl.id);

        if (availableVariant) {
          this.currentVariantId = availableVariant.id;
          this.getCurrentOptions();
          return;
        }
      }

      if (!this.variantFromUrl && this.variant) {
        this.currentVariantId = this.variant.id;
        this.getCurrentOptions();
        return;
      }

      /**
       * Pick the first in stock variant, which is available.
       * - If product has size option then select first available 908g variant.
       * - If no variants are in stock then use first variant.
       */
      let validVariants = this.product?.variants?.filter(
        (variant) =>
          variant.available && variant.availableState === 'available',
      );

      if (this.sizeOptionIndex) {
        validVariants = this.product?.variants?.filter((variant) => {
          return (
            variant.available ||
            variant.selectedOptions[this.sizeOptionIndex]?.value ===
              this.$variable('product.defaultSizeValue')
          );
        });
      }

      /**
       * If no valid variants then select first non-hidden variant.
       * - If a product has no non-hidden variants then it should be hidden.
       */
      let currentVariant =
        this.product?.variants.find(
          (variant) => variant.availableState !== 'hidden',
        ) ?? this.product?.variants[0];

      if (validVariants.length > 0) {
        currentVariant = this.checkAvailability(validVariants[0].id);

        if (this.product.type === 'bundle') {
          currentVariant = validVariants[0];
        }
      }

      /**
       * Only set current variant if none of the variant options are excluded
       * from being initially set.
       * - Except where excluded option has only a single value, otherwise if
       *   single value options are not shown the variant will not be
       *   selectable by the user.
       */
      const currentOptions = currentVariant?.selectedOptions.map(
        (selectedOption) => {
          const optionHandle = convertToHandle(selectedOption.name);
          const matchedOption = this.options.find(
            (option) => option.handle === optionHandle,
          );

          const excludedOption =
            this.$variable('product.excludedInitialSelectOptions').includes(
              selectedOption.name,
            ) && matchedOption.values.length > 1;

          return excludedOption ? '' : selectedOption.value;
        },
      );

      const filteredCurrentOptions = currentOptions.filter(Boolean);

      if (
        filteredCurrentOptions?.length !== currentVariant.selectedOptions.length
      ) {
        this.currentOptions = currentOptions;
        return;
      }

      this.currentVariantId = currentVariant?.id;
      this.getCurrentOptions();
    },

    /**
     * Check availability of the variant.
     * - if it's not available, we select the first available variant
     * @param {Number} variantId - id of the variant from the url
     */
    checkAvailability(variantId) {
      const getCurrentVariant = this.product.variants.find(
        (variant) => variant.id === variantId,
      );

      if (
        getCurrentVariant &&
        getCurrentVariant?.availableState === 'available' &&
        getCurrentVariant?.available
      ) {
        return getCurrentVariant;
      }

      if (getCurrentVariant?.availableState === 'hidden') {
        return this.product?.variants.find(
          (variant) => variant.availableState !== 'hidden',
        );
      }

      const allVariantsUnavailable = this.product?.variants.every(
        (variant) =>
          variant.available === false ||
          variant.availableState === 'hidden' ||
          variant.availableState === 'sold-out',
      );

      if (allVariantsUnavailable) {
        return getCurrentVariant;
      }

      // Fall back to the requested variant if none of the product's other
      // variants are in a plain 'available' state either (e.g. 'back-soon').
      return (
        this.product?.variants.find(
          (variant) =>
            variant.availableState === 'available' && variant.available,
        ) ?? getCurrentVariant
      );
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1));
      const variantId = Number(params.get('variant'));
      if (location.pathname.includes('products_preview')) {
        return this.getVariantFromProperty(
          this.product.selectedOrFirstAvailableVariant.id,
        );
      }

      return this.getVariantFromProperty(variantId);
    },

    /**
     * Get variant object from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object}
     */
    getVariantFromProperty(value, property = 'id') {
      return this.product.variants?.find((variant) => {
        return variant[property] === value;
      });
    },

    /**
     * Get current options.
     */
    getCurrentOptions() {
      if (!this.variant) {
        this.currentOptions = [];
        return;
      }

      for (const [
        index,
        selectedOption,
      ] of this.variant.selectedOptions.entries()) {
        this.currentOptions[index] = selectedOption.value;
      }
    },

    /**
     * Get select options for product-custom-select component.
     * @param {Object} option - Current option object.
     * @returns {Array}
     */
    getSelectOptions(option) {
      const options = [];

      // Add available options
      option.values.forEach((value) => {
        if (!value.hidden && !value.soldOut) {
          options.push({
            key: value.key,
            label: value.value,
            value: value.value,
            disabled: false,
            flavorImage: value.flavorImage || null,
            flavorBadge: value.flavorBadge || null,
          });
        }
      });

      // Add disabled (sold out) options
      option.values.forEach((value) => {
        if (!value.hidden && value.soldOut) {
          options.push({
            key: value.key,
            label: value.value,
            value: value.value,
            disabled: true,
            flavorImage: value.flavorImage || null,
            flavorBadge: value.flavorBadge || null,
          });
        }
      });

      return options;
    },

    /**
     * Handle option change from custom-select.
     * @param {Number} index - Option index.
     * @param {String} value - New value.
     */
    handleOptionChange(index, value) {
      this.currentOptions[index] = value;
    },
  },
};
</script>

<style lang="scss">
@import './product-options';
</style>
